import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, 
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Dashboard
export const dashboardAPI = {
  getKPIs: () => api.get('/dashboard/kpis'),
  getCharts: (period = 'monthly') => api.get('/dashboard/charts', { params: { period } }),
  getActivity: (limit = 20) => api.get('/dashboard/activity', { params: { limit } }),
};

// Stories
export const storiesAPI = {
  getAll: (params) => api.get('/stories', { params }),
  getCounts: () => api.get('/stories/counts'),
  getOne: (id) => api.get(`/stories/${id}`),
  update: (id, payload) => api.put(`/stories/${id}`, payload),
  getAnalytics: (id, period = '7d') => api.get(`/stories/${id}/analytics`, { params: { period } }),
  refreshAnalytics: (id) => api.post(`/stories/${id}/analytics/refresh`),
  // Newsroom lifecycle
  publish: (id) => api.post(`/stories/${id}/publish`),
  approve: (id) => api.post(`/stories/${id}/approve`),
  unpublish: (id) => api.post(`/stories/${id}/unpublish`),
  review: (id) => api.post(`/stories/${id}/review`),
  submit: (id) => api.post(`/stories/${id}/submit`),
  saveDraft: (id) => api.post(`/stories/${id}/save-draft`),
  remove: (id) => api.delete(`/stories/${id}`),
  restore: (id) => api.post(`/stories/${id}/restore`),
  permanent: (id) => api.delete(`/stories/${id}/permanent`),
  bulkPublicDate: (items) => api.post('/stories/bulk-public-date', { items }),
  getSuppressed: (params) => api.get('/feeds/stories/suppressed', { params }),
  unsuppress:    (id)     => api.post(`/feeds/stories/suppressed/${id}/unsuppress`, {}),

};

// Campaign Analytics
export const campaignsAPI = {
  getAll: (params) => api.get('/campaigns', { params }),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

// Client Reports
export const reportsAPI = {
  getAll: (params) => api.get('/reports', { params }),
  generate: (data) => api.post('/reports/generate', data),
  getOne: (id) => api.get(`/reports/${id}`),
  update: (id, data) => api.put(`/reports/${id}`, data),
  exportPDF: (id) => api.post(`/reports/${id}/export-pdf`),
  getPublic: (shareToken) => api.get(`/public/report/${shareToken}`),
};

// Audience Contacts
export const audienceContactsAPI = {
  getAll: (params) => api.get('/audience/contacts', { params }),
  create: (data) => api.post('/audience/contacts', data),
  bulkImport: (formData) => api.post('/audience/contacts/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, data) => api.put(`/audience/contacts/${id}`, data),
  delete: (id) => api.delete(`/audience/contacts/${id}`),
};

// Audience Lists
export const audienceListsAPI = {
  getAll: (params) => api.get('/audience/lists', { params }),
  create: (data) => api.post('/audience/lists', data),
  getOne: (id) => api.get(`/audience/lists/${id}`),
  addContacts: (id, contactIds) => api.post(`/audience/lists/${id}/contacts`, contactIds),
  removeContact: (listId, contactId) => api.delete(`/audience/lists/${listId}/contacts/${contactId}`),
};

// Audience Campaigns
export const audienceCampaignsAPI = {
  getAll: (params) => api.get('/audience/campaigns', { params }),
  create: (data) => api.post('/audience/campaigns', data),
  getOne: (id) => api.get(`/audience/campaigns/${id}`),
  getRecipients: (id) => api.get(`/audience/campaigns/${id}/recipients`),
  send: (id) => api.post(`/audience/campaigns/${id}/send`),
};

// Data Intelligence
export const intelligenceAPI = {
  getAll: (params) => api.get('/intelligence', { params }),
  create: (data) => api.post('/intelligence', data),
  bulkImport: (formData, recordType) => api.post('/intelligence/bulk', formData, {
    params: { record_type: recordType },
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getOne: (id) => api.get(`/intelligence/${id}`),
  update: (id, data) => api.put(`/intelligence/${id}`, data),
  delete: (id) => api.delete(`/intelligence/${id}`),
  getGraph: (id, depth = 1) => api.get(`/intelligence/graph/${id}`, { params: { depth } }),
  extract: (id) => api.post(`/intelligence/${id}/extract`),
};

// Automated Stories
export const autoStoriesAPI = {
  getTemplates: (params) => api.get('/auto-stories/templates', { params }),
  createTemplate: (data) => api.post('/auto-stories/templates', data),
  getTemplate: (id) => api.get(`/auto-stories/templates/${id}`),
  updateTemplate: (id, data) => api.put(`/auto-stories/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/auto-stories/templates/${id}`),
  runTemplate: (id, variables = {}) => api.post(`/auto-stories/templates/${id}/run`, { variables }),
  getRuns: (params) => api.get('/auto-stories/runs', { params }),
  getRun: (id) => api.get(`/auto-stories/runs/${id}`),
  approveRun: (id) => api.post(`/auto-stories/runs/${id}/approve`),
  rejectRun: (id, reason = '') => api.post(`/auto-stories/runs/${id}/reject`, null, { params: { reason } }),
  rerun: (id) => api.post(`/auto-stories/runs/${id}/rerun`),
};

// Geocoding
export const geocodeAPI = {
  geocode: (postcode) => api.get(`/geocode/${postcode}`),
  bulkGeocode: (postcodes) => api.post('/geocode/bulk', postcodes),
};

// Settings
export const settingsAPI = {
  getClickSend: () => api.get('/settings/clicksend'),
  saveClickSend: (data) => api.post('/settings/clicksend', data),
  testClickSend: () => api.post('/settings/clicksend/test'),
  getGamma: () => api.get('/settings/gamma'),
  saveGamma: (data) => api.post('/settings/gamma', data),
  testGamma: () => api.post('/settings/gamma/test'),
  getGrok: () => api.get('/settings/grok'),
  saveGrok: (data) => api.post('/settings/grok', data),
  testGrok: () => api.post('/settings/grok/test'),
  getAnalytics: (publicationId) => api.get(`/settings/analytics/${publicationId}`),
  saveAnalytics: (data) => api.post('/settings/analytics', data),
  testAnalytics: (publicationId) => api.post(`/settings/analytics/${publicationId}/test`),
};


export const inferenceAPI = {
  getStatus: () => api.get('/inference/status'),

  getEndpoints: () => api.get('/inference/endpoints'),
  createEndpoint: (data) => api.post('/inference/endpoints', data),
  updateEndpoint: (id, data) => api.put(`/inference/endpoints/${id}`, data),
  deleteEndpoint: (id) => api.delete(`/inference/endpoints/${id}`),
  testEndpoint: (id) => api.post(`/inference/endpoints/${id}/test`),
  probeEndpoint: (data) => api.post('/inference/endpoints/probe', data),

  getModels: () => api.get('/inference/models'),
  createModel: (data) => api.post('/inference/models', data),
  updateModel: (id, data) => api.put(`/inference/models/${id}`, data),
  deleteModel: (id) => api.delete(`/inference/models/${id}`),
  permanentDeleteModel: (id) => api.delete(`/inference/models/${id}/permanent`),

  getPrompts: (layer) => api.get('/inference/prompts', { params: layer ? { layer } : {} }),
  getActivePrompt: (layer) => api.get(`/inference/prompts/active/${layer}`),
  getPromptVersion: (id) => api.get(`/inference/prompts/${id}`),
  savePrompt: (data) => api.post('/inference/prompts', data),
  activatePrompt: (id) => api.post(`/inference/prompts/${id}/activate`),

  getImagePrompt: () => api.get('/inference/image-prompt'),
  saveImagePrompt: (data) => api.put('/inference/image-prompt', data),

  getGpuStatus: () => api.get('/inference/gpu/status'),
  setVastCredential: (apiKey) => api.put('/inference/gpu/credential', { api_key: apiKey }),
  deleteVastCredential: () => api.delete('/inference/gpu/credential'),
  getInstances: () => api.get('/inference/gpu/instances'),
  setInstance: (instanceId) => api.put('/inference/gpu/instance', { vast_instance_id: instanceId }),
  startInstance: () => api.post('/inference/gpu/instance/start'),
  stopInstance: () => api.post('/inference/gpu/instance/stop'),
  waitInstanceReady: (timeoutSeconds = 300) =>
    api.post('/inference/gpu/instance/wait-ready', { timeout_seconds: timeoutSeconds }),

  getWindows: () => api.get('/inference/gpu/windows'),
  createWindow: (data) => api.post('/inference/gpu/windows', data),
  updateWindow: (id, data) => api.put(`/inference/gpu/windows/${id}`, data),
  deleteWindow: (id) => api.delete(`/inference/gpu/windows/${id}`),
  openWindow: (id) => api.post(`/inference/gpu/windows/${id}/open`),
  closeWindow: () => api.post('/inference/gpu/windows/close'),

  getScheduler: () => api.get('/inference/gpu/scheduler'),
  runTick: () => api.post('/inference/gpu/scheduler/tick'),
  startScheduler: () => api.post('/inference/gpu/scheduler/start'),
  stopScheduler: () => api.post('/inference/gpu/scheduler/stop'),
};


export const automationAPI = {
  getStatus: () => api.get('/automation/status'),
  getJobs: (params) => api.get('/automation/jobs', { params }),

  queueItem: (itemId, modelId) =>
    api.post(`/automation/queue/item/${itemId}`, modelId ? { model_id: modelId } : {}),
  queuePending: (data) => api.post('/automation/queue/pending', data || {}),

  drain: (data) => api.post('/automation/drain', data || {}),
  stopDrain: () => api.post('/automation/drain/stop'),

  cancelJob: (jobId) => api.post(`/automation/jobs/${jobId}/cancel`),
  retryJob: (jobId) => api.post(`/automation/jobs/${jobId}/retry`),

  getSettings: () => api.get('/automation/settings'),
  updateSettings: (data) => api.put('/automation/settings', data),

  updateSource: (sourceId, data) => api.put(`/automation/sources/${sourceId}`, data),
};


// Reference Data
export const referenceAPI = {
  getClients: () => api.get('/clients'),
  getPublications: () => api.get('/publications'),
  getBylines: () => api.get('/bylines'),
  getOrganisations: () => api.get('/organisations'),
  createOrganisation: (data) => api.post('/organisations', data),
  updateOrganisation: (id, data) => api.put(`/organisations/${id}`, data),
  deleteOrganisation: (id) => api.delete(`/organisations/${id}`),
  getTags: (publicationId) => api.get('/tags', { params: { publication_id: publicationId } }),
};

// Global Search
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

// Field Options (Dynamic Dropdowns)
export const fieldOptionsAPI = {
  getAll: (params) => api.get('/field-options', { params }),
  getByType: (fieldType, params) => api.get(`/field-options/${fieldType}`, { params }),
  create: (data) => api.post('/field-options', data),
  update: (id, data) => api.put(`/field-options/${id}`, data),
  delete: (id) => api.delete(`/field-options/${id}`),
  seedDefaults: () => api.post('/field-options/seed-defaults'),
};

export default api;